import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { TrendingUp } from 'lucide-react';
import StockService from '../../Api_Module/Api_Services/StockService';
import { useTheme } from '../ThemeContext';

interface ForecastData {
  symbol: string;
  date: string;
  revenueLow: number;
  revenueHigh: number;
  revenueAvg: number;
  ebitdaLow: number;
  ebitdaHigh: number;
  ebitdaAvg: number;
  ebitLow: number;
  ebitHigh: number;
  ebitAvg: number;
  netIncomeLow: number;
  netIncomeHigh: number;
  netIncomeAvg: number;
  sgaExpenseLow: number;
  sgaExpenseHigh: number;
  sgaExpenseAvg: number;
  epsAvg: number;
  epsHigh: number;
  epsLow: number;
  numAnalystsRevenue: number;
  numAnalystsEps: number;
  // NEW optional flag from backend to indicate historical (actual) data
  historyIndicator?: boolean; // when true: only show *High as actual value

  // Historical single-value fields (present when historyIndicator true)
  revenue?: number;
  eps?: number;
  ebitda?: number;
  netIncome?: number;
  sellingGeneralAndAdministrativeExpenses?: number;
}

const FinancialForecastCharts: React.FC = () => {
  const { symbol: routeSymbol } = useParams<{ symbol?: string }>();
  const reduxSymbol = useSelector((state: any) => state.app?.currentSymbol);
  const symbol = reduxSymbol || routeSymbol || '';
  const { theme } = useTheme();

  const tickColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#111827';
  const axisGridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';
  const labelFill = theme === 'dark' ? '#cbd5e1' : '#111827';

  const [forecastData, setForecastData] = useState<ForecastData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<'eps' | 'revenue' | 'ebitda' | 'netIncome' | 'sga'>('eps');
  const [period, setPeriod] = useState<'quarter' | 'annual'>('quarter');
  const [selectedEstimate, setSelectedEstimate] = useState<'low' | 'medium' | 'high'>('medium');
  const [isSmall, setIsSmall] = useState<boolean>(false);
  const chartAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => setIsSmall(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!symbol) return;
      setLoading(true);
      setError(null);
      try {
        const data = await StockService.getAnalystEstimates(symbol, period);
        if (!cancelled) {
          setForecastData(data || []);
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error('Error fetching analyst projections:', e);
          setError(e?.message || 'Unable to load analyst projections');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [symbol, period]);

  const metricLabelMap: Record<typeof selectedMetric, string> = {
    eps: 'EPS',
    revenue: 'Revenue',
    ebitda: 'EBITDA',
    netIncome: 'Net Income',
    sga: 'SG&A'
  } as const;

  const formatYAxis = (value: number) => {
    if (selectedMetric === 'eps') {
      return `$${value.toFixed(2)}`;
    } else {
      // Use billions for >= 1B, otherwise millions
      const abs = Math.abs(value);
      if (abs >= 1_000_000_000) {
        return `$${(value / 1_000_000_000).toFixed(1)}B`;
      }
      return `$${(value / 1_000_000).toFixed(1)}M`;
    }
  };

  const baseNameMap: Record<typeof selectedMetric, string> = {
    revenue: 'revenue',
    eps: 'eps',
    ebitda: 'ebitda',
    netIncome: 'netIncome',
    sga: 'sgaExpense'
  };

  const formatDateLabel = (dateStr: string): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  // Build chart data - include both historical and forecast data
  const chartData = forecastData.map((item) => {
    const base = baseNameMap[selectedMetric];
    const isHistorical = item.historyIndicator;

    if (isHistorical) {
      // For historical data, use the actual value (stored in the single-value field)
      let actualValue = 0;
      if (selectedMetric === 'eps') {
        actualValue = item.eps ?? 0;
      } else if (selectedMetric === 'revenue') {
        actualValue = item.revenue ?? 0;
      } else if (selectedMetric === 'ebitda') {
        actualValue = item.ebitda ?? 0;
      } else if (selectedMetric === 'netIncome') {
        actualValue = item.netIncome ?? 0;
      } else if (selectedMetric === 'sga') {
        actualValue = item.sellingGeneralAndAdministrativeExpenses ?? 0;
      }

      return {
        date: item.date,
        displayDate: formatDateLabel(item.date),
        actual: actualValue,
        isHistorical: true,
      };
    } else {
      // For forecast data, use low/avg/high estimates
      const low = (item as any)[base + 'Low'];
      const avg = (item as any)[base + 'Avg'];
      const high = (item as any)[base + 'High'];

      return {
        date: item.date,
        displayDate: formatDateLabel(item.date),
        low: low ?? 0,
        medium: avg ?? 0,
        high: high ?? 0,
        isHistorical: false,
      };
    }
  });

  const nextForecast = chartData.find(d => !d.isHistorical);

  // Calculate Y-axis domain for better visualization
  const getYAxisDomain = (): [number, number] => {
    const allValues: number[] = [];
    chartData.forEach(d => {
      if ('actual' in d && d.actual != null) allValues.push(d.actual);
      if ('low' in d && d.low != null) allValues.push(d.low);
      if ('medium' in d && d.medium != null) allValues.push(d.medium);
      if ('high' in d && d.high != null) allValues.push(d.high);
    });

    if (allValues.length === 0) return [0, 100];

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min;

    // Add 15% padding on top and bottom for better visualization
    const padding = range * 0.15;
    const domainMin = Math.max(0, min - padding);
    const domainMax = max + padding;

    return [domainMin, domainMax];
  };

  return (
    <div className="bg-theme-secondary/30 rounded-lg border border-theme-tertiary/30 p-4 md:h-[350px] flex flex-col">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-base font-bold text-theme-primary flex items-center gap-2">
            <TrendingUp className="text-cyan-400" size={16} />
            {symbol} {metricLabelMap[selectedMetric]} Forecast
          </h3>
          {/* Subtitle removed per request to free space */}
        </div>
        <a
          href="https://stockfortress.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] select-none transition-colors whitespace-nowrap pt-1"
          style={{ color: 'rgba(255, 255, 255, 0.4)' }}
        >
          Powered by StockFortress
        </a>
      </div>

      <div className="flex-1 flex flex-col">
        <div
          className="relative h-[260px] md:flex-1"
          style={{ width: '100%', minHeight: 0, touchAction: isSmall ? 'none' as any : 'pan-y' as any }}
          ref={chartAreaRef}
          onPointerDown={(e) => {
            if (!isSmall || e.pointerType !== 'touch') return;
            const target = (document.elementFromPoint(e.clientX, e.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
            if (target) {
              (target as Element).dispatchEvent(new MouseEvent('mouseover', { bubbles: true, clientX: e.clientX, clientY: e.clientY } as any));
              (target as Element).dispatchEvent(new MouseEvent('mousemove', { bubbles: true, clientX: e.clientX, clientY: e.clientY } as any));
            }
          }}
          onPointerMove={(e) => {
            if (!isSmall || e.pointerType !== 'touch') return;
            const target = (document.elementFromPoint(e.clientX, e.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
            if (target) {
              (target as Element).dispatchEvent(new MouseEvent('mousemove', { bubbles: true, clientX: e.clientX, clientY: e.clientY } as any));
            }
          }}
          onPointerUp={(e) => {
            if (!isSmall || e.pointerType !== 'touch') return;
            const target = (document.elementFromPoint(e.clientX, e.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
            if (target) {
              (target as Element).dispatchEvent(new MouseEvent('mouseleave', { bubbles: true, clientX: e.clientX, clientY: e.clientY } as any));
            }
          }}
          onTouchStart={(e) => {
            if (!isSmall) return;
            const t = e.touches[0] || e.changedTouches[0];
            const target = (t && document.elementFromPoint(t.clientX, t.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
            if (t && target) {
              if ('PointerEvent' in window) {
                (target as Element).dispatchEvent(new PointerEvent('pointerover', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
                (target as Element).dispatchEvent(new PointerEvent('pointermove', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
              } else {
                (target as Element).dispatchEvent(new MouseEvent('mouseover', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
                (target as Element).dispatchEvent(new MouseEvent('mousemove', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
              }
            }
          }}
          onTouchMove={(e) => {
            if (!isSmall) return;
            const t = e.touches[0] || e.changedTouches[0];
            const target = (t && document.elementFromPoint(t.clientX, t.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
            if (t && target) {
              if ('PointerEvent' in window) {
                (target as Element).dispatchEvent(new PointerEvent('pointermove', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
              } else {
                (target as Element).dispatchEvent(new MouseEvent('mousemove', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
              }
            }
          }}
          onTouchEnd={(e) => {
            if (!isSmall) return;
            const t = e.changedTouches[0];
            const target = (t && document.elementFromPoint(t.clientX, t.clientY)) || chartAreaRef.current?.querySelector('svg.recharts-surface') || chartAreaRef.current?.querySelector('.recharts-wrapper');
            if (t && target) {
              if ('PointerEvent' in window) {
                (target as Element).dispatchEvent(new PointerEvent('pointerleave', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
              } else {
                (target as Element).dispatchEvent(new MouseEvent('mouseleave', { bubbles: true, clientX: t.clientX, clientY: t.clientY } as any));
              }
            }
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 18, left: isSmall ? 4 : 8, bottom: 0 }}
              barCategoryGap={isSmall ? '10%' : '20%'}
              barGap={isSmall ? 2 : 4}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={axisGridColor} />
              <XAxis
                dataKey="displayDate"
                stroke={tickColor}
                fontSize={isSmall ? 9 : 10}
                interval={isSmall ? 'preserveStartEnd' : 0}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                domain={getYAxisDomain()}
                tickFormatter={formatYAxis}
                stroke={tickColor}
                fontSize={isSmall ? 10 : 10}
                tickLine={false}
                axisLine={false}
                tickCount={isSmall ? 5 : 6}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(20, 20, 30, 0.95)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: "var(--text-primary)",
                  borderRadius: '0.5rem',
                  fontSize: '0.75rem'
                }}
                labelStyle={{ color: "var(--text-primary)", fontWeight: 'bold', marginBottom: '4px' }}
                cursor={false}
                isAnimationActive={false}
                formatter={(value: any, name: string) => {
                  if (value == null) return [null, ''];
                  const labelMap: Record<string, string> = {
                    low: 'Low',
                    medium: 'Medium',
                    high: 'High',
                    actual: 'Actual'
                  };
                  return [formatYAxis(Number(value)), labelMap[name] || name];
                }}
              />
              {/* Historical actual values */}
              <Bar dataKey="actual" stackId="a" fill="#3B82F6" name="actual" radius={[4, 4, 0, 0]} barSize={isSmall ? 14 : undefined}>
                {!isSmall && (
                  <LabelList dataKey="actual" position="top" formatter={(v: any) => formatYAxis(Number(v))} fill={labelFill} fontSize={10} />
                )}
              </Bar>
              {/* Forecast bars - show only selected estimate */}
              {selectedEstimate === 'low' && (
                <Bar dataKey="low" stackId="a" fill="#F97316" name="low" radius={[4, 4, 0, 0]} barSize={isSmall ? 14 : undefined}>
                  {!isSmall && (
                    <LabelList dataKey="low" position="top" formatter={(v: any) => formatYAxis(Number(v))} fill={labelFill} fontSize={10} />
                  )}
                </Bar>
              )}
              {selectedEstimate === 'medium' && (
                <Bar dataKey="medium" stackId="a" fill="#06B6D4" name="medium" radius={[4, 4, 0, 0]} barSize={isSmall ? 14 : undefined}>
                  {!isSmall && (
                    <LabelList dataKey="medium" position="top" formatter={(v: any) => formatYAxis(Number(v))} fill={labelFill} fontSize={10} />
                  )}
                </Bar>
              )}
              {selectedEstimate === 'high' && (
                <Bar dataKey="high" stackId="a" fill="#10B981" name="high" radius={[4, 4, 0, 0]} barSize={isSmall ? 14 : undefined}>
                  {!isSmall && (
                    <LabelList dataKey="high" position="top" formatter={(v: any) => formatYAxis(Number(v))} fill={labelFill} fontSize={10} />
                  )}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
          <div className="absolute end-5 bottom-0 text-[10px] text-theme-muted/40 pointer-events-none select-none">
            stockfortress.com
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-end gap-2 mt-2">
          <div className="flex gap-3 text-[9px] items-center flex-wrap">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 bg-[#3B82F6] rounded-full" />
              <span className="text-theme-secondary">Actual</span>
            </div>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setSelectedEstimate('high')}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedEstimate('high')}
              className={`inline-flex items-center gap-1.5 px-1.5 py-[2px] rounded-md cursor-pointer select-none transition-colors ${selectedEstimate === 'high' ? 'ring-1 ring-blue-500/70' : 'opacity-60 hover:opacity-100'
                }`}
            >
              <div className="w-2.5 h-2.5 bg-[#10B981] rounded-full" />
              <span className="text-theme-secondary">High</span>
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setSelectedEstimate('medium')}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedEstimate('medium')}
              className={`inline-flex items-center gap-1.5 px-1.5 py-[2px] rounded-md cursor-pointer select-none transition-colors ${selectedEstimate === 'medium' ? 'ring-1 ring-blue-500/70' : 'opacity-60 hover:opacity-100'
                }`}
            >
              <div className="w-2.5 h-2.5 bg-[#06B6D4] rounded-full" />
              <span className="text-theme-secondary">Medium</span>
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setSelectedEstimate('low')}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedEstimate('low')}
              className={`inline-flex items-center gap-1.5 px-1.5 py-[2px] rounded-md cursor-pointer select-none transition-colors ${selectedEstimate === 'low' ? 'ring-1 ring-blue-500/70' : 'opacity-60 hover:opacity-100'
                }`}
            >
              <div className="w-2.5 h-2.5 bg-[#F97316] rounded-full" />
              <span className="text-theme-secondary">Low</span>
            </span>
          </div>
          {nextForecast && 'medium' in nextForecast && (
            <div className="flex items-center gap-3 mt-2 sm:mt-0">
              <div className="flex items-center gap-1">
                <TrendingUp className="text-green-400" size={12} />
                <span className="text-[9px] font-semibold text-theme-primary uppercase">
                  NEXT {period === 'quarter' ? 'QUARTER' : 'YEAR'}
                </span>
              </div>
              <div className="text-sm font-bold text-theme-primary">
                {selectedEstimate === 'low' && formatYAxis(Number(nextForecast.low))}
                {selectedEstimate === 'medium' && formatYAxis(Number(nextForecast.medium))}
                {selectedEstimate === 'high' && formatYAxis(Number(nextForecast.high))}
              </div>
              <div className="text-[9px] text-theme-secondary whitespace-nowrap">
                Range: {formatYAxis(Number(nextForecast.low))} - {formatYAxis(Number(nextForecast.high))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
          {(['eps', 'revenue', 'ebitda', 'netIncome', 'sga'] as typeof selectedMetric[]).map(m => (
            <button
              key={m}
              onClick={() => setSelectedMetric(m)}
              className={`px-2 py-1 text-[10px] font-semibold rounded btn btn-xs flex-shrink-0 whitespace-nowrap ${selectedMetric === m ? 'active' : ''
                }`}
            >
              {metricLabelMap[m]}
            </button>
          ))}
        </div>
        <div className="flex gap-2 sm:ml-auto w-full sm:w-auto justify-start sm:justify-end">
          {(['quarter', 'annual'] as ('quarter' | 'annual')[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-2 py-1 text-[10px] font-semibold rounded btn btn-xs flex-shrink-0 whitespace-nowrap ${period === p ? 'active' : ''
                }`}
            >
              {p === 'quarter' ? 'Quarterly' : 'Annual'}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="mt-2 text-[11px] text-theme-secondary">Loading projections...</div>}
      {error && !loading && <div className="mt-2 text-[11px] text-red-400">{error}</div>}
      {!loading && !error && forecastData.length === 0 && (
        <div className="mt-2 text-[11px] text-theme-secondary">No projection data.</div>
      )}
    </div>
  );
};

export default FinancialForecastCharts;

